/**
 * Secure Ruffle Bridge - A secure communication layer between Flash/Ruffle and the client
 * This replaces the vulnerable global window.ruffle object with a token-based system
 */

export default class SecureRuffleBridge {
    constructor(ruffleController) {
        this.controller = ruffleController
        this.sessionToken = this.generateSecureToken()
        this.activeBridge = null
        this.allowedOrigins = ['http://localhost', 'https://localhost'] // Add your domains
        
        // Bind methods to preserve context
        this.createBridge = this.createBridge.bind(this)
        this.destroyBridge = this.destroyBridge.bind(this)
        this.validateAccess = this.validateAccess.bind(this)
        
        // Basic browser API protection
        this.setupBrowserAPIProtection()
    }

    /**
     * Basic protection against browser API abuse
     */
    setupBrowserAPIProtection() {
        // Freeze critical objects to prevent modification
        if (typeof Object.freeze === 'function') {
            Object.freeze(this.validateAccess)
            Object.freeze(this.generateSecureToken)
        }
        
        // Store original references to detect tampering
        this._originalConsole = console.warn
        this._originalSetTimeout = setTimeout
        
        // Detect if critical browser APIs have been modified
        this._browserApiIntegrityCheck = setInterval(() => {
            if (console.warn !== this._originalConsole) {
                console.error('[SecureRuffleBridge] Browser API tampering detected - console modified')
            }
        }, 5000) // Check every 5 seconds
    }

    /**
     * Generates a cryptographically secure token
     */
    generateSecureToken() {
        const array = new Uint8Array(32)
        crypto.getRandomValues(array)
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
    }

    /**
     * Creates a secure bridge for Flash communication
     * Only called from within the RuffleController context
     */
    createBridge() {
        if (this.activeBridge) {
            this.destroyBridge()
        }

        // Create a unique namespace for this session
        const bridgeNamespace = `__ruffle_bridge_${this.sessionToken}`
        const api = typeof this.controller.getBridgeApi === 'function'
            ? this.controller.getBridgeApi()
            : {}
        const keys = typeof this.controller.getBridgeKeys === 'function'
            ? this.controller.getBridgeKeys()
            : []
        
        this.activeBridge = {
            __validateToken: (token) => this.validateAccess(token),
            getKeys: (token) => {
                if (!this.validateAccess(token)) {
                    return null
                }

                return Array.isArray(keys) ? [...keys] : []
            }
        }

        for (const [name, handler] of Object.entries(api)) {
            if (typeof handler !== 'function') {
                continue
            }

            this.activeBridge[name] = (token, ...args) => {
                if (!this.validateAccess(token)) {
                    return null
                }

                try {
                    return handler(...args)
                } catch (error) {
                    console.error(`[SecureRuffleBridge] Error invoking ${name}:`, error)
                    return null
                }
            }
        }

        // Set the bridge on window with the unique namespace
        window[bridgeNamespace] = this.activeBridge
        
        // Store reference for Flash to find the bridge
        window.__ruffle_bridge_token = this.sessionToken
        window.__ruffle_bridge_namespace = bridgeNamespace
        
        // Bridge created successfully
        
        return this.sessionToken
    }

    /**
     * Validates access token for security
     */
    validateAccess(token) {
        // Quick integrity check
        if (typeof token !== 'string' || typeof this.sessionToken !== 'string') {
            console.warn('[SecureRuffleBridge] Type tampering detected in token validation')
            return false
        }
        
        if (!token || token !== this.sessionToken) {
            console.warn('[SecureRuffleBridge] Invalid or missing access token')
            return false
        }
        
        // Additional security checks can be added here
        // e.g., origin validation, rate limiting, etc.
        
        return true
    }

    /**
     * Destroys the current bridge and cleans up
     */
    destroyBridge() {
        if (this.activeBridge) {
            const bridgeNamespace = `__ruffle_bridge_${this.sessionToken}`
            delete window[bridgeNamespace]
            delete window.__ruffle_bridge_token
            delete window.__ruffle_bridge_namespace
            
            this.activeBridge = null
            // Bridge destroyed successfully
        }
        
        // Clean up integrity checking
        if (this._browserApiIntegrityCheck) {
            clearInterval(this._browserApiIntegrityCheck)
            this._browserApiIntegrityCheck = null
        }
        
        // Generate new token for next session
        this.sessionToken = this.generateSecureToken()
    }

    /**
     * Get the current session token (for internal use only)
     */
    getSessionToken() {
        return this.sessionToken
    }
}